import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import SeatPlanSVG from "@/components/SeatPlanSVG";
import plan from "@/data/Plan-6.json";
import { fetchEventById } from "@/apiEndpoints";
import {
    fetchSeatAssignmentsByEvent,
    bulkCreateSeatAssignments,
    type SeatAssignment,
    type CreateSeatAssignmentRequest
} from "@/api/seatAssignmentApi";

interface EventTicket {
    id: string;
    ticket_number: string;
    event_name: string;
    price: number;
    category: string;
    customer_name?: string;
    customer_email?: string;
    assigned_seat_uuid?: string;
}

const SeatPlan = () => {
    // Get event ID from URL params
    const { eventId } = useParams<{ eventId: string }>();

    const [data, setData] = useState<any>([
        { type: 'reserved', seat_uuid: '1d669725-f3d0-40fc-b7e0-501f65a38bfe' },
        { type: 'disabled', seat_uuid: '7ebd2b47-45dc-4705-9e2b-b246c7564387' }
    ]);

    // Fetch event data
    const { data: eventData, isLoading: isEventLoading, isError: isEventError } = useQuery({
        queryKey: ['event', eventId],
        queryFn: () => fetchEventById(eventId!),
        enabled: !!eventId,
    });

    // Tickets are now included in event data
    const ticketsData = eventData?.data?.tickets ? { data: eventData.data.tickets } : null;
    const isTicketsLoading = isEventLoading;
    const isTicketsError = isEventError;

    // Fetch existing seat assignments for the event
    const { data: seatAssignmentsData, isLoading: isSeatAssignmentsLoading } = useQuery({
        queryKey: ['seat-assignments', eventId],
        queryFn: () => fetchSeatAssignmentsByEvent(eventId!),
        enabled: !!eventId,
    });

    // Local state for ticket assignments - maps seat UUID to ticket ID and status
    const [ticketAssignments, setTicketAssignments] = useState<{ [seatUuid: string]: { ticketId: string; status: string } }>({});

    // Transform API tickets to our EventTicket format
    const tickets: EventTicket[] = ticketsData?.data?.map((ticket: any, index: number) => ({
        id: ticket._id || `ticket-${index}`,
        ticket_number: ticket.name || `T${String(index + 1).padStart(3, '0')}`,
        event_name: eventData?.data?.title || 'Event',
        price: ticket.price || 0,
        category: ticket.type || 'General',
        customer_name: ticket.customer_name || '',
        customer_email: ticket.customer_email || '',
        assigned_seat_uuid: ticket.assigned_seat_uuid
    })) || [];

    const handleTicketAssignment = (ticketId: string, seatUuids: string | string[]) => {
        const uuids = Array.isArray(seatUuids) ? seatUuids : [seatUuids];
        setTicketAssignments(prev => {
            const newAssignments = { ...prev };
            uuids.forEach(uuid => {
                newAssignments[uuid] = { ticketId, status: 'assigned' };
            });
            return newAssignments;
        });
        console.log('Ticket assigned:', ticketId, 'to seats:', uuids);
    };

    // Handle bulk ticket assignment - assign same ticket type to multiple seats
    const handleBulkTicketAssignment = (ticketId: string, seatUuids: string[]) => {
        setTicketAssignments(prev => {
            const newAssignments = { ...prev };
            seatUuids.forEach(seatUuid => {
                newAssignments[seatUuid] = { ticketId, status: 'assigned' };
            });
            return newAssignments;
        });
        console.log('Bulk assignment:', ticketId, 'to seats:', seatUuids);
    };

    const handleTicketUnassignment = (seatUuid: string) => {
        setTicketAssignments(prev => {
            const newAssignments = { ...prev };
            delete newAssignments[seatUuid];
            return newAssignments;
        });
        console.log('Ticket unassigned from seat:', seatUuid);
    };

    // Save assignments to backend
    const saveAssignments = async () => {
        try {
            if (!eventId || !eventData?.data?.venueId) {
                alert('Missing event ID or venue ID');
                return;
            }

            // Convert ticketAssignments to API format
            const assignments: CreateSeatAssignmentRequest[] = Object.entries(ticketAssignments).map(([seatUuid, assignment]) => ({
                eventId: eventId!,
                venueId: eventData.data.venueId,
                ticketId: assignment.ticketId,
                seatUuid,
                status: assignment.status
            }));

            if (assignments.length === 0) {
                alert('No assignments to save');
                return;
            }

            // Create new assignments (backend will clear existing ones automatically)
            const result = await bulkCreateSeatAssignments({
                eventId: eventId!,
                assignments
            });

            console.log('Assignments saved successfully:', result);
            alert(`Successfully saved ${assignments.length} seat assignments!`);

            // Refresh the assignments data
            window.location.reload(); // Simple refresh for now
        } catch (error) {
            console.error('Failed to save assignments:', error);
            alert('Failed to save assignments. Please try again.');
        }
    };


    // Show loading state
    if (isEventLoading || isTicketsLoading || isSeatAssignmentsLoading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen">
                <div className="text-lg font-semibold mb-4">Loading Event Data...</div>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <div className="text-sm text-gray-500 mt-4">
                    Event ID: {eventId || 'None'}
                </div>
            </div>
        );
    }

    // Show error state
    if (isEventError || isTicketsError) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen">
                <div className="text-lg font-semibold mb-4 text-red-600">Error Loading Data</div>
                <div className="text-sm text-gray-600 mb-4">
                    {isEventError ? 'Failed to load event data' : 'Failed to load tickets'}
                </div>
                <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                    Retry
                </button>
            </div>
        );
    }

    // Show message if no event ID is provided
    if (!eventId) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen">
                <div className="text-lg font-semibold mb-4">No Event Selected</div>
                <div className="text-sm text-gray-600 mb-4">
                    Please provide an event ID in the URL
                </div>
                <div className="text-xs text-gray-500 max-w-md text-center">
                    Navigate to /seat-plan/:eventId to use the seat plan for a specific event
                </div>
                <div className="mt-4">
                    <button
                        onClick={() => window.location.href = '/seat-plan/test-event-id'}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        Test with Sample Event
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-center">
            <SeatPlanSVG
                plan={eventData?.data?.venue?.seatMap as any}
                seatAssignmentsWithOrderInfo={seatAssignmentsData?.data || []}
                data={data}
                availableTickets={tickets}
                ticketAssignments={ticketAssignments}
                onTicketAssignment={handleTicketAssignment}
                onTicketUnassignment={handleTicketUnassignment}
            />

            {/* Display assignment status */}
            <div style={{
                position: "fixed",
                bottom: "20px",
                left: "20px",
                padding: "8px 12px",
                backgroundColor: "#007bff",
                color: "white",
                borderRadius: "4px",
                fontSize: "14px",
                fontWeight: "bold",
                zIndex: 1000
            }}>
                Assigned: {Object.keys(ticketAssignments).length} / {tickets.length} tickets
            </div>

            {/* Save assignments button */}
            {Object.keys(ticketAssignments).length > 0 && (
                <button
                    onClick={saveAssignments}
                    style={{
                        position: "fixed",
                        bottom: "20px",
                        right: "20px",
                        padding: "8px 16px",
                        backgroundColor: "#28a745",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        fontSize: "14px",
                        fontWeight: "bold",
                        cursor: "pointer",
                        zIndex: 1000
                    }}
                >
                    Save Assignments
                </button>
            )}

            {/* Event info display - Top Left */}
            {eventData?.data && (
                <div style={{
                    position: "fixed",
                    top: "20px",
                    left: "20px",
                    padding: "16px",
                    backgroundColor: "#ffffff",
                    border: "2px solid #007bff",
                    borderRadius: "12px",
                    fontSize: "14px",
                    maxWidth: "350px",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    zIndex: 1000,
                    backdropFilter: "blur(10px)"
                }}>
                    <div style={{
                        fontWeight: "bold",
                        fontSize: "18px",
                        marginBottom: "8px",
                        color: "#007bff"
                    }}>
                        {eventData.data.title}
                    </div>

                    <div style={{ marginBottom: "6px" }}>
                        <span style={{ fontWeight: "600", color: "#333" }}>Category:</span>
                        <span style={{ color: "#666", marginLeft: "8px" }}>{eventData.data.category}</span>
                    </div>

                    <div style={{ marginBottom: "6px" }}>
                        <span style={{ fontWeight: "600", color: "#333" }}>Venue:</span>
                        <span style={{ color: "#666", marginLeft: "8px" }}>{eventData.data.venue}</span>
                    </div>

                    {eventData.data.address && (
                        <div style={{ marginBottom: "6px" }}>
                            <span style={{ fontWeight: "600", color: "#333" }}>Address:</span>
                            <span style={{ color: "#666", marginLeft: "8px", fontSize: "12px" }}>
                                {typeof eventData.data.address === 'string'
                                    ? eventData.data.address
                                    : eventData.data.address.fullAddress ||
                                    `${eventData.data.address.city}, ${eventData.data.address.state}` ||
                                    'Address available'
                                }
                            </span>
                        </div>
                    )}

                    {eventData.data.times && eventData.data.times.length > 0 && (
                        <div style={{ marginBottom: "6px" }}>
                            <span style={{ fontWeight: "600", color: "#333" }}>Date:</span>
                            <span style={{ color: "#666", marginLeft: "8px", fontSize: "12px" }}>
                                {new Date(eventData.data.times[0].startDate).toLocaleDateString()}
                                {eventData.data.times[0].startTime && ` at ${eventData.data.times[0].startTime}`}
                            </span>
                        </div>
                    )}

                    <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginTop: "12px",
                        paddingTop: "8px",
                        borderTop: "1px solid #e9ecef"
                    }}>
                        <div style={{ fontSize: "12px", color: "#666" }}>
                            <span style={{ fontWeight: "600" }}>Tickets:</span> {tickets.length} available
                        </div>
                        <div style={{ fontSize: "12px", color: "#666" }}>
                            <span style={{ fontWeight: "600" }}>Assigned:</span> {Object.keys(ticketAssignments).length}
                        </div>
                    </div>

                    {eventData.data.organizer && (
                        <div style={{
                            marginTop: "8px",
                            paddingTop: "8px",
                            borderTop: "1px solid #e9ecef",
                            fontSize: "12px"
                        }}>
                            <span style={{ fontWeight: "600", color: "#333" }}>Organizer:</span>
                            <span style={{ color: "#666", marginLeft: "8px" }}>
                                {eventData.data.organizer.name}
                            </span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SeatPlan;